import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiMail, FiLock, FiUser, FiKey, FiCheck, FiEye, FiEyeOff } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientLoginModal = memo(({ 
  isOpen, 
  onClose, 
  onLoginSuccess, 
  primaryColor = '#d4af37' 
}) => {
  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordResult, setForgotPasswordResult] = useState(null);
  
  // Change password state (for must_change_password flow)
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [pendingUserData, setPendingUserData] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.access_token) {
        // Check if user must change password
        if (data.user.must_change_password) {
          // Store token and user temporarily
          localStorage.setItem('token', data.access_token);
          setPendingUserData(data.user);
          setChangePasswordData(prev => ({ ...prev, current_password: loginData.password }));
          setShowChangePassword(true);
        } else {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          onLoginSuccess(data.user);
          onClose();
        }
      } else {
        setLoginError(data.detail || 'Identifiants invalides');
      }
    } catch (error) {
      setLoginError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordLoading(true);
    setChangePasswordError('');
    
    // Validate
    if (changePasswordData.new_password.length < 6) {
      setChangePasswordError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      setChangePasswordLoading(false);
      return;
    }
    
    if (changePasswordData.new_password !== changePasswordData.confirm_password) {
      setChangePasswordError('Les mots de passe ne correspondent pas');
      setChangePasswordLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(changePasswordData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Update user data without must_change_password flag
        const updatedUser = { ...pendingUserData, must_change_password: false };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        onLoginSuccess(updatedUser);
        onClose();
      } else {
        setChangePasswordError(data.detail || 'Erreur lors du changement de mot de passe');
      }
    } catch (error) {
      setChangePasswordError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail })
      });
      const data = await response.json();
      setForgotPasswordResult(data);
    } catch (error) {
      setForgotPasswordResult({ message: 'Erreur de connexion. Veuillez réessayer.' });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    if (forgotPasswordResult?.temp_password) {
      setLoginData(prev => ({ ...prev, password: forgotPasswordResult.temp_password }));
    }
    setForgotPasswordResult(null);
  };

  const resetModal = () => {
    setShowChangePassword(false);
    setShowForgotPassword(false);
    setPendingUserData(null);
    setChangePasswordData({ current_password: '', new_password: '', confirm_password: '' });
    setChangePasswordError('');
    setLoginError('');
    setForgotPasswordResult(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  // Determine title and subtitle based on current view
  let title = 'Espace Client';
  let subtitle = 'Connectez-vous à votre compte';
  let icon = <FiUser className="h-8 w-8" style={{ color: primaryColor }} />;
  
  if (showChangePassword) {
    title = 'Nouveau mot de passe';
    subtitle = 'Veuillez créer un mot de passe personnel';
    icon = <FiKey className="h-8 w-8" style={{ color: primaryColor }} />;
  } else if (showForgotPassword) {
    title = 'Mot de passe oublié';
    subtitle = 'Recevez un nouveau mot de passe';
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="p-6 text-center relative"
            style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}10)` }}
          >
            {!showChangePassword && (
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/10 transition"
                data-testid="close-login-modal"
              >
                <FiX className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}30` }}
            >
              {icon}
            </div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          </div>

          {/* Content */}
          <div className="p-6">
            {showChangePassword ? (
              // Change Password Form (after login with temp password)
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="text-amber-800 font-medium flex items-center gap-2">
                    <FiKey className="h-4 w-4" />
                    Création obligatoire
                  </p>
                  <p className="text-amber-700 text-xs mt-1">
                    Pour sécuriser votre compte, vous devez créer un nouveau mot de passe personnel.
                  </p>
                </div>
                
                {changePasswordError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {changePasswordError}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showNewPassword ? "text" : "password"}
                      required
                      minLength={6}
                      value={changePasswordData.new_password}
                      onChange={e => setChangePasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                      className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all"
                      placeholder="Minimum 6 caractères"
                      autoComplete="new-password"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="toggle-new-password-visibility"
                    >
                      {showNewPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmer le mot de passe
                  </label>
                  <div className="relative">
                    <FiCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      minLength={6}
                      value={changePasswordData.confirm_password}
                      onChange={e => setChangePasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all"
                      placeholder="Retapez le mot de passe"
                      autoComplete="new-password"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="toggle-confirm-password-visibility"
                    >
                      {showConfirmPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={changePasswordLoading}
                  className="w-full py-3 rounded-xl font-bold text-black transition-all hover:scale-[1.02] disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="change-password-submit"
                >
                  {changePasswordLoading ? 'Enregistrement...' : 'Créer mon mot de passe'}
                </button>
              </form>
            ) : showForgotPassword ? (
              // Forgot Password Form
              <>
                {forgotPasswordResult && (forgotPasswordResult.message || forgotPasswordResult.temp_password) ? (
                  <div className="text-center">
                    {forgotPasswordResult.temp_password ? (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                        <p className="text-green-800 font-medium mb-2">Votre nouveau mot de passe :</p>
                        <div className="bg-white border border-green-300 rounded-lg p-3 my-3">
                          <p className="font-mono text-xl font-bold select-all" style={{ color: primaryColor }}>
                            {forgotPasswordResult.temp_password}
                          </p>
                        </div>
                        <p className="text-sm text-green-700 font-medium">
                          Cliquez ci-dessous pour vous connecter et créer votre mot de passe définitif.
                        </p>
                      </div>
                    ) : forgotPasswordResult.email_sent ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                        <p className="text-blue-800 font-medium">Email envoyé !</p>
                        <p className="text-blue-700 text-sm mt-2">{forgotPasswordResult.message}</p>
                        <p className="text-blue-600 text-xs mt-2">Vérifiez votre boîte de réception (et les spams).</p>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                        <p className="text-blue-800">{forgotPasswordResult.message}</p>
                      </div>
                    )}
                    <button
                      onClick={handleBackToLogin}
                      className="w-full py-3 rounded-xl font-bold text-black transition-all hover:scale-[1.02]"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="back-to-login-btn"
                    >
                      {forgotPasswordResult.temp_password ? 'Se connecter avec ce mot de passe' : 'Retour à la connexion'}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <p className="text-amber-800 font-medium">Important</p>
                      <p className="text-amber-700 text-xs mt-1">
                        Si vous avez déjà demandé une réinitialisation, utilisez le <strong>dernier</strong> mot de passe reçu.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <div className="relative">
                        <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="email"
                          required
                          value={forgotPasswordEmail}
                          onChange={e => setForgotPasswordEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all"
                          placeholder="votre@email.com"
                          autoComplete="email"
                          data-testid="forgot-email-input"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="w-full py-3 rounded-xl font-bold text-black transition-all hover:scale-[1.02] disabled:opacity-50"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="forgot-password-submit"
                    >
                      {forgotPasswordLoading ? 'Envoi en cours...' : 'Réinitialiser mon mot de passe'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="w-full py-2 text-gray-600 hover:text-gray-900 text-sm"
                    >
                      Retour à la connexion
                    </button>
                  </form>
                )}
              </>
            ) : (
              // Login Form
              <form onSubmit={handleLogin} className="space-y-4">
                {loginError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {loginError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={loginData.email}
                      onChange={e => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all"
                      placeholder="votre@email.com"
                      autoComplete="email"
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      required
                      value={loginData.password}
                      onChange={e => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all"
                      placeholder="••••••••"
                      autoComplete="current-password"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showLoginPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-3 rounded-xl font-bold text-black transition-all hover:scale-[1.02] disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? 'Connexion...' : 'Se connecter'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setForgotPasswordEmail(loginData.email);
                    setForgotPasswordResult(null);
                  }}
                  className="w-full py-2 text-sm hover:underline"
                  style={{ color: primaryColor }}
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});

ClientLoginModal.displayName = 'ClientLoginModal';

export default ClientLoginModal;
